import axios from '@/lib/axios'
import { useAuthStore } from '@/stores/auth'
import { echo } from '@laravel/echo-vue'
import { useInfiniteQuery, useQueryClient } from '@tanstack/vue-query'
import { get } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { type MaybeRef, computed, onUnmounted, ref, watch } from 'vue'
import { useToast } from 'vue-toastification'

import type { CustomAxiosRequestConfig } from '@/interfaces/customAxiosRequestConfig'

import type {
	AiReportAlertas,
	AiReportGenerateOptions,
	AiReportGenerationParams,
	AiReportHistoryFilters,
	AiReportRequestedBy,
	AiReportUsage,
	AiTravelReport,
	AiTravelReportResponse,
	CompanyTravelReportFailedEvent,
	CompanyTravelReportGeneratedEvent,
} from '@/modules/reports/interfaces/aiTravelReport'

interface AiReportListItem {
	id: number
	companyId: number
	content?: string | null
	period?: string | null
	generatedAt: string
	summary?: string | null
	shortSummary?: string | null
	alerts?: AiReportAlertas | null
	recommendationsCount?: number | null
	requestedBy?: AiReportRequestedBy | null
	usage?: AiReportUsage | null
	params?: AiReportGenerationParams | null
}

// meta llega camelizada por ParseToCamelAfter (currentPage, lastPage…)
interface AiReportListResponse {
	data: AiReportListItem[]
	meta: {
		currentPage: number
		lastPage: number
		perPage: number
		total: number
	}
}

export const getAiReport = async (
	companyId: number,
	reportId: number
): Promise<AiTravelReport> => {
	const { data } = await axios.get<{ report: AiTravelReport }>(
		`/api/companies/${companyId}/reports/ai/${reportId}`,
		{ errorHandling: false } as CustomAxiosRequestConfig
	)
	return data.report
}

export interface AiReportsPage {
	items: AiTravelReport[]
	meta: {
		currentPage: number
		lastPage: number
		perPage: number
		total: number
	}
}

export const getAiReports = async (
	companyId: number,
	filters: AiReportHistoryFilters,
	page = 1
): Promise<AiReportsPage> => {
	const params: Record<string, string | number> = { page }
	if (filters.severity) params.severity = filters.severity
	if (filters.from) params.from = filters.from
	if (filters.to) params.to = filters.to
	if (filters.perPage) params.per_page = filters.perPage
	if (filters.minRecommendations)
		params.min_recommendations = filters.minRecommendations

	const { data } = await axios.get<AiReportListResponse>(
		`/api/companies/${companyId}/reports/ai`,
		{ params, errorHandling: false } as CustomAxiosRequestConfig
	)

	return {
		items: data.data.map((r) => ({
			id: r.id,
			companyId: r.companyId,
			content: r.content,
			generatedAt: r.generatedAt,
			period: r.period,
			summary: r.summary,
			shortSummary: r.shortSummary,
			alerts: r.alerts,
			recommendationsCount: r.recommendationsCount,
			requestedBy: r.requestedBy,
			usage: r.usage,
			params: r.params,
		})),
		meta: data.meta,
	}
}

const useAiTravelReport = (companyId: MaybeRef<number>) => {
	const toast = useToast()
	const queryClient = useQueryClient()
	const { user } = storeToRefs(useAuthStore())

	// El medidor semanal (useAiReportUsage) debe avanzar en cuanto una generación
	// consume cuota: al despachar (202) y al llegar el evento de terminado.
	const invalidateUsage = () =>
		queryClient.invalidateQueries({ queryKey: ['aiReportUsage'] })

	const resolvedCompany = computed(() => get(companyId))

	const report = ref<AiTravelReport | null>(null)
	const isGenerating = ref(false)
	const error = ref<string | null>(null)
	const isThrottled = ref(false)

	const historyFilters = ref<AiReportHistoryFilters>({})

	// Multi-empresa: al cambiar de empresa el reporte en pantalla es de la
	// anterior — se limpia (el historial se recarga solo vía queryKey).
	watch(resolvedCompany, (next, prev) => {
		if (prev !== undefined && next !== prev) {
			report.value = null
			error.value = null
		}
	})

	// ─── Historial (scroll infinito) ──────────────────────────────────────────

	const {
		data: historyData,
		isLoading: isLoadingHistory,
		refetch: refetchHistory,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
	} = useInfiniteQuery({
		queryKey: computed(() => [
			'aiReports',
			resolvedCompany.value,
			historyFilters.value,
		]),
		queryFn: ({ pageParam = 1 }) =>
			getAiReports(resolvedCompany.value, historyFilters.value, pageParam),
		getNextPageParam: (lastPage) =>
			lastPage.meta.currentPage < lastPage.meta.lastPage
				? lastPage.meta.currentPage + 1
				: undefined,
		enabled: computed(() => !!resolvedCompany.value),
		retry: false,
		refetchOnWindowFocus: false,
	})

	const history = computed(
		() => historyData.value?.pages.flatMap((page) => page.items) ?? []
	)

	const historyTotal = computed(
		() => historyData.value?.pages[0]?.meta.total ?? 0
	)

	// ─── WebSocket ───────────────────────────────────────────────────────────
	// useEcho bakes the channel name statically — we watch user.id and subscribe
	// manually so the channel name is always correct even if user loads async.
	// The layout-level listener (useAiReportNotifications) owns the channel
	// lifecycle; here we only add/remove our own handler so unmounting the page
	// doesn't kill the global subscription.

	const onReportGenerated = async (e: CompanyTravelReportGeneratedEvent) => {
		const id = e.report_id ?? e.reportId
		const cid = resolvedCompany.value
		if (!id || !cid) {
			isGenerating.value = false
			return
		}
		try {
			report.value = await getAiReport(cid, id)
		} catch {
			// getAiReport failed — report may appear via history refetch
		}
		isGenerating.value = false
		refetchHistory()
		invalidateUsage()
	}

	// La generación falló en la cola (proveedor caído o respuesta incompleta). Sin este
	// evento el spinner se quedaba girando para siempre. El back ya devolvió la cuota,
	// así que el medidor se refresca.
	const onReportFailed = (e: CompanyTravelReportFailedEvent) => {
		if (e.company_id && e.company_id !== resolvedCompany.value) return
		isGenerating.value = false
		error.value =
			e.message || 'No se pudo generar el análisis. Intenta de nuevo.'
		toast.error(error.value)
		invalidateUsage()
	}

	let echoCleanup: (() => void) | null = null

	watch(
		() => user.value?.id,
		(userId) => {
			echoCleanup?.()
			echoCleanup = null
			if (!userId) return

			const channelName = `ai-reports.user.${userId}`
			echo()
				.private(channelName)
				.listen('CompanyTravelReportGenerated', onReportGenerated)
				.listen('CompanyTravelReportFailed', onReportFailed)

			echoCleanup = () =>
				echo()
					.private(channelName)
					.stopListening('CompanyTravelReportGenerated', onReportGenerated)
					.stopListening('CompanyTravelReportFailed', onReportFailed)
		},
		{ immediate: true }
	)

	// ─── Generate ────────────────────────────────────────────────────────────

	const generate = async (options: AiReportGenerateOptions = {}) => {
		if (isGenerating.value) return
		error.value = null
		isGenerating.value = true

		// `dates`/`focus` solo viajan cuando difieren del default: el body participa
		// en el filters_hash del backend, y omitirlos mantiene el cache de los
		// reportes previos (todo el histórico, enfoque general).
		const body: Record<string, unknown> = {}
		if (options.dates) body.dates = options.dates
		if (options.focus && options.focus !== 'general') body.focus = options.focus

		try {
			const { data } = await axios.post<AiTravelReportResponse>(
				`/api/companies/${resolvedCompany.value}/reports/ai/travel${options.fresh ? '?fresh=1' : ''}`,
				body,
				{ errorHandling: false } as CustomAxiosRequestConfig
			)

			if (data.status === 'cached' || data.status === 'completed') {
				report.value = data.report ?? null
				isGenerating.value = false
				refetchHistory()
			} else {
				// processing → ya consumió cuota; el medidor avanza desde ya
				invalidateUsage()
			}
		} catch (err: unknown) {
			isGenerating.value = false
			const status = (err as { response?: { status?: number } })?.response
				?.status
			if (status === 429) {
				isThrottled.value = true
				error.value = 'Límite alcanzado (5 por minuto). Intenta en un momento.'
				setTimeout(() => {
					isThrottled.value = false
				}, 60_000)
			} else {
				// Mensaje del server primero (p.ej. 422 "Alcanzaste el límite de N
				// reportes de IA de tu plan este mes…") con fallback genérico.
				const serverMessage = (
					err as { response?: { data?: { message?: string } } }
				)?.response?.data?.message
				error.value =
					serverMessage || 'No se pudo generar el reporte. Intenta de nuevo.'
				toast.error(error.value)
			}
		}
	}

	onUnmounted(() => {
		echoCleanup?.()
		echoCleanup = null
	})

	return {
		report,
		history,
		historyTotal,
		historyFilters,
		isLoadingHistory,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		isGenerating,
		isThrottled,
		error,
		generate,
	}
}

export default useAiTravelReport
